/**
 * 会动的 TA（P2a 播放器的 React 适配）。
 * - 有能播的动作清单：按播放器逐帧画；目标状态（target）变了就按清单里的路线动过去，事件（onEvent）按帧发出。
 * - 没有能播的清单（没给、头部坏了、身份对不上、一段合格的都没有）：原样交给 PetFigure 画静态姿势——旧静态姿势的语义一点不变。
 * - 新回包里某段坏了而手上那段是好的：留着手上那段（好段不被坏段顶掉）。
 * - 某段的帧加载不出来：这段不再播，落到它的回落状态继续；稳定姿势也加载不出来：退回 PetFigure。
 *   这个退回只算这一轮身份：换了身份（含换回原来那只）重新加载，同一轮里不反复重试。
 *   挂上时先把各段的帧预取一遍：提前发现缺帧的段就绕开它，播放时换帧也不用现等下载。
 *   预取用的 Image 一直留到这一轮结束：浏览器的内存缓存靠它们保着，帧地址回 no-store 也不会每换一帧重下
 *   （MOTION-PLAYER-M2 在系统 Chrome 上用真组件实测：播放 10 秒重下 0 次，每次请求慢 80ms 时也是 0）。
 * - onStatus 只在状态、当前段、缺帧的段、已加载的帧变了时报，同一段里换帧不再每帧报一次（报的内容本来就不含帧号；MOTION-PLAYER-M2）。
 * - 页面在后台时不走时钟，回来不补播；减少动态效果时不播过渡也不播循环，直接落到目标状态的稳定姿势。
 * - 跳过去的那一下（MOTION-CROSSFADE-117）：没有过渡段可走（fallback no_path，过渡段还没生效）、或正在播的段缺帧（missing_frame）时，
 *   画面不经过渡直接落到另一个姿势。这时把上一帧原样盖在上层淡出 FADE_MS 毫秒，新姿势在下面立刻就位，换姿势不跳。
 *   减少动态效果、暂停、页面在后台时不叠这一层，直接切；上一帧本身加载失败的也不叠；正常播过渡段（连续动作）不触发。
 * 这里只管画面，不写回 TA 真实在做什么。接进小窝等共享页面由 I 排槽，本组件先独立使用。
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { CharacterPose, CharacterState, PetPrivateSummary } from "@/shared/contracts";
import { PetFigure } from "@/features/home/PetFigure";
import { mergeMotionSet, prepareMotion, type MotionSet, type MotionStateId, type PreparedMotion } from "./manifest";
import { createPlayer, currentFrame, face, markBroken, request, settle, step, type MotionEvent, type MotionPlayer } from "./player";
import { framePlacement, referenceHeightOf } from "./placement";
import "./pet_motion.css";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
/** 跳姿势时上一帧淡出的时长（毫秒）；和 pet_motion.css 的 ps-pet-motion-fade 动画同一个数 */
export const FADE_MS = 240;
/** 这两种回落是「不经过渡直接落到另一个姿势」，要淡化；找不到目标姿势（no_target_pose）时画面不动，不算 */
const JUMPS = new Set(["no_path", "missing_frame"]);

/** 上层淡出的那一帧：地址、几何（和它当时画的一样），key 每跳一次加一，连跳时重新开始淡出 */
interface Fade {
  url: string;
  style: CSSProperties;
  key: number;
}

function usePrefersReducedMotion(): boolean {
  const query = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION) : null;
  const [reduced, setReduced] = useState(() => query?.matches ?? false);
  useEffect(() => {
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, [query]);
  return reduced;
}

export interface PetMotionProps {
  pet: PetPrivateSummary;
  state: CharacterState | null;
  /** 没有能播的清单时交给 PetFigure 的静态姿势 */
  pose?: CharacterPose;
  said?: string | null;
  /** 读口回来的这一套动作（GET /pets/{pet_id}/motion?purpose=…） */
  motion?: MotionSet | null;
  /** 调用方知道这只宠物此刻的身体版本时给；不给就不比 */
  bodyRevision?: number | null;
  /** 要去的状态（清单里没有稳定姿势的状态不理） */
  target?: MotionStateId;
  onEvent?: (event: MotionEvent) => void;
  paused?: boolean;
  /** 不给就跟随系统“减少动态效果”设置 */
  reducedMotion?: boolean;
  /** Optional isolated adapter controls; omitted keeps existing component behavior. */
  facing?: string;
  initialState?: MotionStateId;
  resetKey?: string;
  waitForLoad?: boolean;
  /** Opt-in load gate for the current route; other clips may preload independently. */
  requiredClipKeys?: readonly string[];
  muted?: boolean;
  onStatus?: (status: PetMotionStatus) => void;
}

export interface PetMotionStatus {
  signature: string | null;
  state: MotionStateId | null;
  clipKey: string | null;
  loadedClipKeys: readonly string[];
  /** URLs whose image load, decode and geometry have passed in this player round. */
  loadedFrameUrls: readonly string[];
  brokenClipKeys: readonly string[];
  failed: boolean;
}

export function PetMotion({ pet, state, pose = "neutral_full", said = null, motion = null, bodyRevision, target, onEvent, paused = false, reducedMotion, facing, initialState, resetKey, waitForLoad = false, requiredClipKeys, muted = false, onStatus }: PetMotionProps) {
  const active = state?.active ?? null;
  const knowsBody = bodyRevision !== undefined;
  // 手上最后一份能播的清单：新回包里坏掉的段拿它补（同一套才补）
  const lastGood = useRef<MotionSet | null>(null);
  const cacheRound = useRef(resetKey);
  const prepared = useMemo(() => {
    if (cacheRound.current !== resetKey) { lastGood.current = null; cacheRound.current = resetKey; }
    const merged = mergeMotionSet(lastGood.current, motion);
    const ready = prepareMotion(merged, active ? { set: active, ...(knowsBody ? { bodyRevision } : {}) } : null);
    lastGood.current = ready ? merged : null;
    return ready;
    // 身份按值比：同一套形象的对象重新下发不必重建
  }, [motion, active?.pet_id, active?.reference_version, active?.assets.find((a) => a.pose === "neutral_full")?.content_sha256, knowsBody, bodyRevision, resetKey]);
  // 稳定姿势取不到只记在这一轮上（c84a D0034-2）：签名一变就开新的一轮——换了一只、换了一版、清单撤下，
  // 也包括换回之前失败过的那只——失败标记不带过来，重新加载；同一轮里的普通重渲染保持静态，不反复重试；
  // 旧一轮迟到的失败只认它自己那一轮，碰不到新的一轮。
  const signature = prepared ? `${resetKey ?? ""}::${prepared.signature}` : null;
  const [round, setRound] = useState<MotionRound>(() => ({ signature, failed: false }));
  let current = round;
  if (current.signature !== signature) {
    current = { signature, failed: false };
    setRound(current);
  }
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;
  useEffect(() => {
    if (!prepared || current.failed) statusRef.current?.({ signature: null, state: null, clipKey: null, loadedClipKeys: [], loadedFrameUrls: [], brokenClipKeys: [], failed: current.failed });
  }, [prepared, current.failed]);
  if (!prepared || current.failed) return <PetFigure pet={pet} state={state} pose={pose} said={said} />;
  const owner = current;
  return (
    <MotionFigure
      key={signature}
      pet={pet}
      motion={prepared}
      said={said}
      target={target}
      onEvent={onEvent}
      facing={facing}
      initialState={initialState}
      waitForLoad={waitForLoad}
      requiredClipKeys={requiredClipKeys}
      muted={muted}
      onStatus={onStatus}
      paused={paused}
      reducedMotion={reducedMotion}
      onStableFailed={() => setRound((now) => (now === owner ? { ...now, failed: true } : now))}
    />
  );
}

/** 一轮：同一个签名从挂上到换掉的这段时间 */
interface MotionRound {
  signature: string | null;
  failed: boolean;
}

function MotionFigure({
  pet, motion, said, target, onEvent, paused, reducedMotion, onStableFailed, facing, initialState, waitForLoad, requiredClipKeys, muted, onStatus,
}: { pet: PetPrivateSummary; motion: PreparedMotion; said: string | null; target?: MotionStateId; onEvent?: (event: MotionEvent) => void; paused: boolean; reducedMotion?: boolean; onStableFailed: () => void; facing?: string; initialState?: MotionStateId; waitForLoad: boolean; requiredClipKeys?: readonly string[]; muted: boolean; onStatus?: (status: PetMotionStatus) => void }) {
  const systemReduced = usePrefersReducedMotion();
  const still = reducedMotion ?? systemReduced;
  // 只在挂上时建一次播放器（换了一版清单由外层 key 重挂）
  const [initial] = useState(() => createPlayer(motion, initialState ?? target, facing ?? null));
  const playerRef = useRef<MotionPlayer>(initial);
  // Same semantic signature may carry refreshed signed URLs. Keep state/phase,
  // but point the player and held frame at the current resources, not old URLs.
  const running = playerRef.current;
  if (running.motion !== motion) {
    const clipKey = running.clip?.clip_key;
    const held = running.hold;
    const owner = held ? running.motion.clips.find((c) => c.frames.includes(held)) : null;
    const hold = held ? (owner ? motion.clips.find((c) => c.clip_key === owner.clip_key)?.frames[owner.frames.indexOf(held)] : motion.stable.get(running.state)) ?? null : null;
    playerRef.current = { ...running, motion, clip: clipKey ? motion.clips.find((c) => c.clip_key === clipKey) ?? null : null, hold };
  }
  // 只用来在换帧时触发重画（状态报告不跟着它走，见下面 statusKey）
  const [, setVersion] = useState(0);
  const eventRef = useRef(onEvent);
  eventRef.current = onEvent;
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;
  const alive = useRef(true);
  const controls = useRef({ muted, paused, still });
  controls.current = { muted, paused, still };
  const [loadedUrls, setLoadedUrls] = useState<ReadonlySet<string>>(() => new Set());
  const clipReady = (c: PreparedMotion["clips"][number]) => playerRef.current.broken.has(c.clip_key) || c.frames.every((f) => loadedUrls.has(f.url));
  const loaded = requiredClipKeys ? requiredClipKeys.every((key) => {
    const clip = motion.clips.find((c) => c.clip_key === key);
    return !!clip && clipReady(clip);
  }) : motion.clips.every(clipReady);
  const canTick = !waitForLoad || loaded;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // 加载失败过的帧地址：此刻要画的帧落在里面（比如某状态的稳定姿势本身取不到），整套退回 PetFigure
  const failedUrls = useRef(new Set<string>());
  // 跳姿势时盖在上层淡出的上一帧（MOTION-CROSSFADE-117）
  const [fade, setFade] = useState<Fade | null>(null);

  const apply = (next: { player: MotionPlayer; events: MotionEvent[] }) => {
    if (!alive.current) return;
    const before = playerRef.current;
    const shown = currentFrame(before);
    playerRef.current = next.player;
    for (const event of next.events) {
      if (event.type !== "frame" || (!controls.current.muted && !controls.current.paused && !controls.current.still && document.visibilityState !== "hidden")) eventRef.current?.(event);
    }
    const landed = currentFrame(next.player);
    if (failedUrls.current.has(landed.url)) {
      onStableFailed();
      return;
    }
    const jumped = next.events.some((event) => event.type === "fallback" && JUMPS.has(event.reason));
    if (jumped && landed.url !== shown.url && !failedUrls.current.has(shown.url)
      && !controls.current.paused && !controls.current.still && document.visibilityState !== "hidden") {
      const was = framePlacement(shown, motion.set.canvas, referenceHeightOf(motion));
      setFade((now) => ({ url: shown.url, style: { height: `${was.heightPct}%`, aspectRatio: was.aspectRatio, transform: was.transform }, key: (now?.key ?? 0) + 1 }));
    }
    if (before.clip !== next.player.clip || before.frame !== next.player.frame || before.state !== next.player.state || before.broken !== next.player.broken) setVersion((v) => v + 1);
  };
  // 淡出放完就撤掉这一层（动画结束事件之外再兜一个计时器：后台标签页、没有动画的环境里也撤得掉）；
  // 中途改成减少动态效果或暂停，立刻撤
  useEffect(() => {
    if (!fade) return undefined;
    const timer = window.setTimeout(() => setFade((now) => (now?.key === fade.key ? null : now)), FADE_MS + 60);
    return () => window.clearTimeout(timer);
  }, [fade?.key]);
  useEffect(() => {
    if (still || paused) setFade(null);
  }, [still, paused]);
  const fail = (url: string, clipKey: string | null) => {
    if (!alive.current) return;
    failedUrls.current.add(url);
    apply(clipKey ? markBroken(playerRef.current, clipKey) : { player: playerRef.current, events: [] });
  };

  // 目标变了：出发（减少动态效果时直接落到目标的稳定姿势）
  useEffect(() => {
    if (!canTick) return;
    const moved = target === undefined ? { player: playerRef.current, events: [] } : request(playerRef.current, target);
    if (!still) {
      apply(moved);
      return;
    }
    const landed = settle(moved.player);
    apply({ player: landed.player, events: [...moved.events, ...landed.events] });
  }, [target, still, canTick]);

  // Facing is opt-in. Home filters direction triplets and restarts through STAND,
  // so the generic game's same-frame face behavior is never inferred as phase proof.
  useEffect(() => {
    if (facing !== undefined) apply({ player: face(playerRef.current, facing), events: [] });
  }, [facing]);

  // 报的内容只看状态、当前段、缺帧的段、已加载的帧：每换一帧 setVersion 会触发重画，但报出去的东西不变，不必每帧再报一次
  const reported = playerRef.current;
  const statusKey = `${reported.state}\u0000${reported.clip?.clip_key ?? ""}\u0000${[...reported.broken].join(",")}`;
  useEffect(() => {
    const p = playerRef.current;
    statusRef.current?.({ signature: motion.signature, state: p.state, clipKey: p.clip?.clip_key ?? null,
      loadedClipKeys: motion.clips.filter((c) => !p.broken.has(c.clip_key) && c.frames.every((f) => loadedUrls.has(f.url))).map((c) => c.clip_key),
      loadedFrameUrls: [...loadedUrls],
      brokenClipKeys: [...p.broken], failed: false });
  }, [motion, loadedUrls, statusKey]);

  // 预取各段的帧：哪段有帧取不到，这段就标成缺帧（没在播的段只是以后绕开）
  useEffect(() => {
    if (typeof Image === "undefined") return undefined;
    let cancelled = false;
    const loaders: HTMLImageElement[] = [];
    for (const clip of motion.clips) {
      for (const frame of clip.frames) {
        // Successful/failed resources are bounded to this player round; URL rotation can load a new resource.
        if (loadedUrls.has(frame.url) || failedUrls.current.has(frame.url)) continue;
        const img = new Image();
        img.onerror = () => { if (!cancelled) fail(frame.url, clip.clip_key); };
        img.onload = () => {
          const accept = () => {
            if (cancelled || !alive.current) return;
            if (img.naturalWidth !== motion.set.canvas.width || img.naturalHeight !== motion.set.canvas.height) { fail(frame.url, clip.clip_key); return; }
            setLoadedUrls((previous) => new Set(previous).add(frame.url));
          };
          if (typeof img.decode === "function") void img.decode().then(accept, () => { if (!cancelled) fail(frame.url, clip.clip_key); });
          else accept();
        };
        img.src = frame.url;
        loaders.push(img);
      }
    }
    return () => { cancelled = true; loaders.forEach((img) => { img.onerror = null; img.onload = null; }); };
  }, [motion]);

  // 时钟：只在可见、没暂停、没要求减少动态效果时走；从后台回来重新起算，不补播
  useEffect(() => {
    if (!canTick || paused || still || typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") return undefined;
    let last: number | null = null;
    let handle = 0;
    const tick = (now: number) => {
      if (!alive.current) return;
      if (document.visibilityState === "hidden") last = null;
      else {
        if (last !== null) apply(step(playerRef.current, now - last));
        last = now;
      }
      handle = window.requestAnimationFrame(tick);
    };
    handle = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(handle);
  }, [paused, still, canTick]);

  const player = playerRef.current;
  const frame = currentFrame(player);
  const place = framePlacement(frame, motion.set.canvas, referenceHeightOf(motion));
  const onError = () => {
    const clip = playerRef.current.clip;
    fail(frame.url, clip && clip.frames.includes(frame) ? clip.clip_key : null);
  };
  return (
    <span
      className="ps-pet-motion"
      data-testid="pet-motion"
      data-purpose={motion.set.purpose}
      data-generation={motion.set.generation_method}
      data-state={player.state}
      data-clip={player.clip?.clip_key ?? ""}
      data-frame={player.clip ? player.frame : ""}
    >
      <span className="ps-pet-motion__shadow" aria-hidden="true" />
      {/* 同一个 img 换 src：新帧解码好之前浏览器留着上一帧，不闪白 */}
      <img
        src={frame.url}
        alt={`${pet.name}的星球形象`}
        draggable={false}
        onError={onError}
        style={{ height: `${place.heightPct}%`, aspectRatio: place.aspectRatio, transform: place.transform }}
      />
      {fade ? (
        <img
          key={fade.key}
          className="ps-pet-motion__fade"
          data-testid="pet-motion-fade"
          src={fade.url}
          alt=""
          aria-hidden="true"
          draggable={false}
          style={fade.style}
          onAnimationEnd={() => setFade((now) => (now?.key === fade.key ? null : now))}
        />
      ) : null}
      {said ? <span className="ps-pet-motion__note">{said}</span> : null}
    </span>
  );
}
